import { useState } from 'react';
import { CheckOutlined, CopyOutlined, WarningOutlined } from '@ant-design/icons';

import { Button, Dialog, Field, Input } from '../../../components/ui';
import { useT } from '../../../i18n';
import { useToast } from '../../../lib/toast';
import { GIU_DA_CHEP_MS } from '../constants';
import type { ApiKeyCreated } from '../types';

/**
 * Hộp hiện bí mật của khoá vừa tạo — **một lần duy nhất**. Máy chủ chỉ giữ bản
 * băm nên đóng hộp là không lấy lại được; vì vậy bấm ra ngoài không đóng hộp,
 * chỉ nút "Tôi đã lưu khoá", nút đóng và Esc mới đóng.
 */
export function HopBiMatKhoaApi({ vuaTao, onClose }: { vuaTao: ApiKeyCreated | null; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const [daChep, setDaChep] = useState(false);

  const chep = () => {
    if (!vuaTao) return;
    // Clipboard API chỉ có trên https hoặc localhost; thiếu thì nói để người dùng tự chọn chữ.
    const ghi = navigator.clipboard?.writeText(vuaTao.secret) ?? Promise.reject(new Error('clipboard'));
    ghi.then(
      () => {
        setDaChep(true);
        toast.success(t('khoaApi.daChepToast'));
        window.setTimeout(() => setDaChep(false), GIU_DA_CHEP_MS);
      },
      () => toast.warning(t('khoaApi.khongChepDuoc')),
    );
  };

  return (
    <Dialog
      open={vuaTao !== null}
      onClose={onClose}
      title={t('khoaApi.biMatTieuDe', { ten: vuaTao?.api_key.name ?? '' })}
      width={560}
      maskClosable={false}
      destroyOnHidden
      footer={
        <div className="flex justify-end">
          <Button variant="primary" size="md" onClick={onClose}>
            {t('khoaApi.daLuuKhoa')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 pt-2">
        <p
          role="note"
          className="flex items-start gap-2 rounded bg-warning-50 px-3 py-2.5 text-xs leading-relaxed text-warning-700"
        >
          <WarningOutlined className="mt-0.5 shrink-0" />
          <span className="min-w-0">{t('khoaApi.biMatCanhBao')}</span>
        </p>
        <Field label={t('khoaApi.biMatNhan')} hint={t('khoaApi.biMatGoiY')}>
          {(p) => (
            <div className="flex min-w-0 gap-2">
              <Input
                {...p}
                readOnly
                className="min-w-0 flex-1 font-mono text-xs"
                value={vuaTao?.secret ?? ''}
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button
                variant="secondary"
                size="md"
                icon={daChep ? <CheckOutlined /> : <CopyOutlined />}
                onClick={chep}
              >
                {daChep ? t('khoaApi.daChep') : t('khoaApi.saoChep')}
              </Button>
            </div>
          )}
        </Field>
      </div>
    </Dialog>
  );
}
